import { useCallback, useEffect, useMemo, useState } from 'react';
import clarificationService from '../service/clarificationService.mjs';
import { claimKey, CHANGED_EVENT } from '../util/clarificationText.mjs';

/**
 * The unresolved questions a page's rows need badges for, in ONE request.
 *
 * With `reportId`, the report's own questions and those on its claims; without
 * it, every unresolved question the reader can see (the auditor's queues).
 * Refreshes whenever a question changes anywhere on the page.
 *
 * @returns {{ forClaim: (claimType, claimId) => Array, reportThreads: Array,
 *             all: Array, refresh: Function }}
 */
export const useOpenQuestions = ({ reportId = null, enabled = true } = {}) => {
  const [all, setAll] = useState([]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      setAll(await clarificationService.list({ status: 'unresolved', ...(reportId ? { reportId } : {}) }));
    } catch {
      // Badges are a convenience; a failed lookup leaves the page as it was.
    }
  }, [enabled, reportId]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onChange = () => refresh();
    window.addEventListener(CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CHANGED_EVENT, onChange);
  }, [enabled, refresh]);

  const byClaim = useMemo(() => {
    const map = new Map();
    for (const t of all) {
      if (t.subjectType !== 'claim') continue;
      const key = claimKey(t.claimType, t.claimId);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return map;
  }, [all]);

  const forClaim = useCallback(
    (claimType, claimId) => byClaim.get(claimKey(claimType, claimId)) || [],
    [byClaim]
  );

  const reportThreads = useMemo(() => all.filter(t => t.subjectType === 'report'), [all]);

  return { forClaim, reportThreads, all, refresh };
};

export default useOpenQuestions;
